import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import Header from "../components/Header";
const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";

export default function Signup() {
  const [form, setForm] = useState({ name: "", email: "", password: "", bio: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) navigate("/dashboard");
  }, [navigate]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setError("Full name is required.");
      return;
    }
    if (!form.email.trim()) {
      setError("Email is required.");
      return;
    }
    if (form.password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (!acceptedTerms) {
      setError("You must agree to the Terms of Use to sign up.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API_URL}/creators/signup`, form);
      alert("Account created! Please check your email and click the verification link. After verification, an admin will approve your account.");
      navigate("/login");
    } catch (err) {
      setError(err.response?.data?.error || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-8">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8">
        <h1 className="text-3xl font-bold text-center text-primary mb-2">Become a Creator</h1>
        <p className="text-center text-gray-500 mb-8">Share your Bible explanations</p>

        {error && (
          <div className="bg-red-50 text-red-600 p-3 rounded-md mb-6 text-center text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <input
            name="name"
            placeholder="Full Name"
            value={form.name}
            onChange={handleChange}
            className="w-full px-4 py-3 mb-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            required
            autoFocus
          />
          <input
            name="email"
            type="email"
            placeholder="Email"
            value={form.email}
            onChange={handleChange}
            className="w-full px-4 py-3 mb-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
            required
          />
          <div className="relative mb-4">
            <input
              name="password"
              type={showPassword ? "text" : "password"}
              placeholder="Password (min. 6 characters)"
              value={form.password}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
            >
              {showPassword ? "Hide" : "Show"}
            </button>
          </div>
          <textarea
            name="bio"
            placeholder="Short bio (optional)"
            value={form.bio}
            onChange={handleChange}
            rows="3"
            className="w-full px-4 py-3 mb-4 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
          />

          <div className="flex items-start gap-2 mb-6">
            <input
              type="checkbox"
              id="terms"
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="mt-1"
            />
            <label htmlFor="terms" className="text-sm text-gray-600">
              I agree to the{" "}
              <button
                type="button"
                onClick={() => setShowTermsModal(true)}
                className="text-primary hover:underline"
              >
                Terms of Use
              </button>{" "}
              and confirm that I have read the Creator Guidelines (available on the dashboard).
            </label>
          </div>

          <button
            type="submit"
            disabled={loading || !acceptedTerms}
            className="w-full bg-primary text-white py-3 rounded-md font-semibold hover:bg-opacity-90 transition disabled:opacity-50"
          >
            {loading ? "Creating account..." : "Sign Up"}
          </button>
        </form>

        <p className="text-center text-gray-500 mt-6 text-sm">
          Already have an account?{" "}
          <Link to="/login" className="text-primary hover:underline">
            Sign in
          </Link>
        </p>
      </div>

      {/* Terms Modal – same content as before, but styled cleanly */}
      {showTermsModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={() => setShowTermsModal(false)}
        >
          <div
            className="bg-white rounded-lg max-w-lg w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 className="text-2xl font-bold p-6 border-b">Terms of Use</h2>
            <div className="flex-1 overflow-y-auto p-6 text-gray-700 text-sm space-y-4">
              <p><strong>1. Acceptance of Terms</strong><br/>By signing up as a Creator on VerSeer, you agree to these Terms of Use.</p>
              <p><strong>2. Content Ownership & License</strong><br/>You retain ownership of your original video content. However, by submitting a video, you grant VerSeer a perpetual, worldwide, royalty‑free license to host, publish, display, and distribute your video on the VerSeer mobile app, website, and associated YouTube channel. You also allow us to edit, crop, or add watermarks as needed.</p>
              <p><strong>3. Content Guidelines</strong><br/>Videos must be respectful, theologically sound, and focused on explaining the Bible verse. No hate speech, personal attacks, false doctrine, or misleading information. VerSeer reserves the right to reject or remove any content that violates these guidelines.</p>
              <p><strong>4. Limits & Review</strong><br/>All submitted videos are reviewed by VerSeer administrators before publication. You agree to abide by the weekly upload limits, file size restrictions, and other technical guidelines shown on your dashboard.</p>
              <p><strong>5. Termination</strong><br/>VerSeer may suspend or terminate your creator account at any time if you violate these terms or if your content is deemed harmful to the community.</p>
              <p><strong>6. Disclaimer</strong><br/>VerSeer does not endorse any personal theological views expressed in creator videos. Users are encouraged to read the Bible for themselves. The platform is provided "as is" without warranties.</p>
              <p><strong>7. Changes to Terms</strong><br/>We may update these terms from time to time. Continued use of your account constitutes acceptance of the updated terms.</p>
            </div>
            <button
              onClick={() => setShowTermsModal(false)}
              className="m-4 p-2 bg-primary text-white rounded-md hover:bg-opacity-90"
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}